// Story 2.4 — Admin drop-state cockpit page at /admin/drops.
//
// Four status sections (Scheduled / Live / Retiring / Retired —
// retired collapsed by default). URL-synced filter bar (kind, status,
// from, to). Per-row action button for the legal next transition only.
// Three transition dialogs: ReleaseEarlyDialog (scheduled→live),
// ForceRetiringDialog (live→retiring), ForceCloseDialog (retiring→retired
// with typed confirmation per AC9).
//
// Reads via useAdminDropsList (15s staleTime); writes via
// useAdminTransitionDrop which invalidates the list on success.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
} from "@greenhold/components";
import toast from "react-hot-toast";

import { EmptyState } from "../components/EmptyState";

import {
  AdminDropRow,
  AdminDropsFilters,
  DropKind,
  DropStatus,
  EMPTY_DROPS_FILTERS,
  filtersFromSearchParams,
  searchParamsFromFilters,
  useAdminDropsList,
} from "../features/admin/drops/useAdminDropsList";
import {
  AdminDropTransitionError,
  DropTransitionTarget,
  useAdminTransitionDrop,
} from "../features/admin/drops/useAdminTransitionDrop";

// --------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------

function formatEur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function statusBadgeProps(status: string): {
  variant: "success" | "warning" | "secondary" | "default";
  className?: string;
} {
  switch (status) {
    case "scheduled":
      return { variant: "warning" };
    case "live":
      return { variant: "success" };
    case "retiring":
      return { variant: "warning", className: "ring-2 ring-warning-500" };
    case "retired":
      return { variant: "secondary" };
    default:
      return { variant: "default" };
  }
}

function statusAriaLabel(row: AdminDropRow): string {
  switch (row.status) {
    case "scheduled":
      return `Scheduled to go live on ${formatDateTime(row.scheduled_at)}`;
    case "live":
      return `Live since ${formatDateTime(row.live_at)}`;
    case "retiring":
      return "Retiring — 2 weeks left";
    case "retired":
      return `Retired at ${formatDateTime(row.retired_at)}`;
    default:
      return row.status;
  }
}

// --------------------------------------------------------------------
// Dialog state
// --------------------------------------------------------------------

type DialogState =
  | { kind: "none" }
  | { kind: "release-early"; row: AdminDropRow }
  | { kind: "force-retiring"; row: AdminDropRow }
  | { kind: "force-close"; row: AdminDropRow };

// --------------------------------------------------------------------
// Page
// --------------------------------------------------------------------

export function AdminDropsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );
  const query = useAdminDropsList(filters);
  const transition = useAdminTransitionDrop();
  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });

  function updateFilter<K extends keyof AdminDropsFilters>(
    key: K,
    value: AdminDropsFilters[K] | null,
  ) {
    const next: AdminDropsFilters = { ...filters, [key]: value, page: 1 };
    setSearchParams(searchParamsFromFilters(next));
  }

  function clearFilters() {
    setSearchParams(searchParamsFromFilters(EMPTY_DROPS_FILTERS));
  }

  const hasAnyFilter =
    filters.kind !== null ||
    filters.status !== null ||
    filters.from !== null ||
    filters.to !== null;

  function openDialog(row: AdminDropRow) {
    switch (row.status) {
      case "scheduled":
        setDialog({ kind: "release-early", row });
        break;
      case "live":
        setDialog({ kind: "force-retiring", row });
        break;
      case "retiring":
        setDialog({ kind: "force-close", row });
        break;
      default:
        break;
    }
  }

  function closeDialog() {
    if (transition.isPending) return;
    setDialog({ kind: "none" });
  }

  async function runTransition(
    row: AdminDropRow,
    to: DropTransitionTarget,
    confirmationToken?: string,
  ) {
    try {
      const result = await transition.mutateAsync({
        drop_id: row.drop_id,
        to_status: to,
        confirmation_token: confirmationToken,
      });
      toast.success(`Drop is now ${result.data.status}.`);
      setDialog({ kind: "none" });
    } catch (e) {
      if (e instanceof AdminDropTransitionError) {
        if (e.code === "DROP_TRANSITION_INVALID") {
          toast.error(
            `Drop already moved to '${e.currentStatus ?? "unknown"}'. Refreshing.`,
          );
          setDialog({ kind: "none" });
          return;
        }
        if (e.code === "DROP_TRANSITION_CONFIRMATION_MISMATCH") {
          toast.error("The confirmation text doesn't match the portfolio title.");
          return;
        }
        if (e.code === "DROP_TRANSITION_WELCOME_FORBIDDEN") {
          toast.error("Welcome drops are managed via the welcome-portfolio flow.");
          return;
        }
        toast.error(e.message);
        return;
      }
      toast.error(e instanceof Error ? e.message : "Transition failed.");
    }
  }

  const items: AdminDropRow[] = query.data?.data.items ?? [];
  const grouped = useMemo(() => {
    const out: Record<DropStatus, AdminDropRow[]> = {
      scheduled: [],
      live: [],
      retiring: [],
      retired: [],
    };
    for (const r of items) {
      if (
        r.status === "scheduled" ||
        r.status === "live" ||
        r.status === "retiring" ||
        r.status === "retired"
      ) {
        out[r.status as DropStatus].push(r);
      }
    }
    return out;
  }, [items]);

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header>
          <h1 className="text-3xl font-heading font-bold">Drop state cockpit</h1>
          <p className="opacity-70 text-sm mt-1">
            View every released drop grouped by status. Trigger early release,
            force the "2 weeks left" warning, or close a drop forever. Every
            transition writes an audit-log row.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label htmlFor="filter-kind">Kind</Label>
              <select
                id="filter-kind"
                className="block w-full mt-1 border border-brand-forest-200 rounded px-2 py-1 bg-white text-sm"
                value={filters.kind ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "kind",
                    (e.target.value || null) as DropKind | null,
                  )
                }
              >
                <option value="">All</option>
                <option value="regular">Regular</option>
                <option value="special">Special</option>
                <option value="welcome">Welcome</option>
              </select>
            </div>
            <div>
              <Label htmlFor="filter-status">Status</Label>
              <select
                id="filter-status"
                className="block w-full mt-1 border border-brand-forest-200 rounded px-2 py-1 bg-white text-sm"
                value={filters.status ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "status",
                    (e.target.value || null) as DropStatus | null,
                  )
                }
              >
                <option value="">All</option>
                <option value="scheduled">Scheduled</option>
                <option value="live">Live</option>
                <option value="retiring">Retiring</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div>
              <Label htmlFor="filter-from">From (scheduled_at)</Label>
              <Input
                id="filter-from"
                type="date"
                value={filters.from ? filters.from.slice(0, 10) : ""}
                onChange={(e) =>
                  updateFilter(
                    "from",
                    e.target.value
                      ? new Date(e.target.value + "T00:00:00Z").toISOString()
                      : null,
                  )
                }
              />
            </div>
            <div>
              <Label htmlFor="filter-to">To (scheduled_at)</Label>
              <Input
                id="filter-to"
                type="date"
                value={filters.to ? filters.to.slice(0, 10) : ""}
                onChange={(e) =>
                  updateFilter(
                    "to",
                    e.target.value
                      ? new Date(e.target.value + "T23:59:59Z").toISOString()
                      : null,
                  )
                }
              />
            </div>
            {hasAnyFilter ? (
              <div className="sm:col-span-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  aria-label="Clear all filters"
                >
                  Clear filters
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {query.isPending ? (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-state-error-fg">
                Failed to load drops: {(query.error as Error).message}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <DropsSection
              status="scheduled"
              label="Scheduled"
              rows={grouped.scheduled}
              onAction={openDialog}
            />
            <DropsSection
              status="live"
              label="Live"
              rows={grouped.live}
              onAction={openDialog}
            />
            <DropsSection
              status="retiring"
              label="Retiring"
              rows={grouped.retiring}
              onAction={openDialog}
            />
            <RetiredSection rows={grouped.retired} />
          </>
        )}
      </div>

      {/* Dialogs */}
      <ReleaseEarlyDialog
        state={dialog}
        pending={transition.isPending}
        onClose={closeDialog}
        onConfirm={(row) => runTransition(row, "live")}
      />
      <ForceRetiringDialog
        state={dialog}
        pending={transition.isPending}
        onClose={closeDialog}
        onConfirm={(row) => runTransition(row, "retiring")}
      />
      <ForceCloseDialog
        state={dialog}
        pending={transition.isPending}
        onClose={closeDialog}
        onConfirm={(row, token) => runTransition(row, "retired", token)}
      />
    </main>
  );
}

// --------------------------------------------------------------------
// Section components
// --------------------------------------------------------------------

function DropsSection({
  status,
  label,
  rows,
  onAction,
}: {
  status: DropStatus;
  label: string;
  rows: AdminDropRow[];
  onAction: (row: AdminDropRow) => void;
}) {
  const badge = statusBadgeProps(status);
  return (
    <section>
      <h2 className="text-xl font-heading font-bold mb-3 flex items-center gap-2">
        <Badge variant={badge.variant} className={badge.className}>
          {label}
        </Badge>
        <span className="opacity-60 text-sm">({rows.length})</span>
      </h2>
      <Card>
        <CardContent className="pt-6">
          {rows.length === 0 ? (
            <EmptyState
              aria-label="No drops"
              description="No drops in this state."
            />
          ) : (
            <DropsTable rows={rows} onAction={onAction} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function RetiredSection({ rows }: { rows: AdminDropRow[] }) {
  return (
    <section>
      <details>
        <summary className="cursor-pointer text-xl font-heading font-bold mb-3 flex items-center gap-2">
          <Badge variant="secondary">Retired</Badge>
          <span className="opacity-60 text-sm">({rows.length})</span>
          <span className="opacity-50 text-xs ml-2">click to expand</span>
        </summary>
        <Card className="mt-3">
          <CardContent className="pt-6">
            {rows.length === 0 ? (
              <EmptyState
                aria-label="No retired drops"
                description="No retired drops."
              />
            ) : (
              <DropsTable rows={rows} onAction={() => {}} />
            )}
          </CardContent>
        </Card>
      </details>
    </section>
  );
}

function DropsTable({
  rows,
  onAction,
}: {
  rows: AdminDropRow[];
  onAction: (row: AdminDropRow) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase opacity-60 border-b border-brand-forest-100">
            <th className="py-2 pr-3">Portfolio</th>
            <th className="py-2 pr-3">Kind</th>
            <th className="py-2 pr-3">Price</th>
            <th className="py-2 pr-3">Scheduled</th>
            <th className="py-2 pr-3">Retires</th>
            <th className="py-2 pr-3">Sold</th>
            <th className="py-2 pr-3">Cap</th>
            <th className="py-2 pr-3">Bulletin</th>
            <th className="py-2 pr-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <DropsRow key={row.drop_id} row={row} onAction={onAction} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DropsRow({
  row,
  onAction,
}: {
  row: AdminDropRow;
  onAction: (row: AdminDropRow) => void;
}) {
  const badge = statusBadgeProps(row.status);
  return (
    <tr className="border-b border-brand-forest-50 hover:bg-brand-forest-50/30">
      <td className="py-2 pr-3">
        <Link
          to={`/admin/portfolios/${row.portfolio_id}`}
          className="underline decoration-dotted"
        >
          {row.portfolio_title}
        </Link>
        <div className="text-xs mt-0.5">
          <Badge
            variant={badge.variant}
            className={badge.className}
            aria-label={statusAriaLabel(row)}
          >
            {row.status}
          </Badge>
        </div>
      </td>
      <td className="py-2 pr-3">
        <Badge variant="secondary">{row.kind}</Badge>
      </td>
      <td className="py-2 pr-3 font-mono">{formatEur(row.price_cents)}</td>
      <td className="py-2 pr-3 text-xs">{formatDateTime(row.scheduled_at)}</td>
      <td className="py-2 pr-3 text-xs">{formatDateTime(row.retiring_at)}</td>
      <td className="py-2 pr-3 font-mono">
        {row.sold_count}
        {row.free_claim_count > 0 ? (
          <span
            className="opacity-60"
            title={`${row.free_claim_count} of ${row.sold_count} claimed free`}
          >
            {" "}
            ({row.free_claim_count})
          </span>
        ) : null}
      </td>
      <td className="py-2 pr-3 font-mono">
        {row.per_user_cap !== null ? `${row.per_user_cap}/user` : "—"}
      </td>
      <td className="py-2 pr-3">
        {row.linked_bulletin_post_id ? (
          <Link
            to={`/admin/bulletin/${row.linked_bulletin_post_id}`}
            className="underline decoration-dotted text-xs"
          >
            {row.linked_bulletin_title ?? row.linked_bulletin_slug ?? "view"}
          </Link>
        ) : (
          <span className="opacity-50 text-xs">—</span>
        )}
      </td>
      <td className="py-2 pr-3">
        <RowAction row={row} onAction={onAction} />
      </td>
    </tr>
  );
}

function RowAction({
  row,
  onAction,
}: {
  row: AdminDropRow;
  onAction: (row: AdminDropRow) => void;
}) {
  if (row.is_perpetual) {
    return (
      <Link
        to="/admin/welcome-portfolio"
        className="underline decoration-dotted text-xs"
      >
        Manage welcome portfolio →
      </Link>
    );
  }
  switch (row.status) {
    case "scheduled":
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onAction(row)}
        >
          Release now…
        </Button>
      );
    case "live":
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onAction(row)}
        >
          Force retiring…
        </Button>
      );
    case "retiring":
      return (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onAction(row)}
        >
          Force close…
        </Button>
      );
    case "retired":
    default:
      return (
        <Link
          to={`/admin/audit-log?event_kind=admin.drop_state_transitioned&subject_id=${row.drop_id}`}
          className="underline decoration-dotted text-xs"
        >
          View audit log →
        </Link>
      );
  }
}

// --------------------------------------------------------------------
// Transition dialogs
// --------------------------------------------------------------------

function ReleaseEarlyDialog({
  state,
  pending,
  onClose,
  onConfirm,
}: {
  state: DialogState;
  pending: boolean;
  onClose: () => void;
  onConfirm: (row: AdminDropRow) => void;
}) {
  const open = state.kind === "release-early";
  const row = state.kind === "release-early" ? state.row : null;
  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Release this drop now?</DialogTitle>
          <DialogDescription>
            {row ? (
              <>
                <strong>{row.portfolio_title}</strong> will be visible in the
                public dossier shop immediately. Users can start buying as soon
                as this completes.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => row && onConfirm(row)}
            disabled={pending || !row}
          >
            {pending ? "Releasing…" : "Release now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForceRetiringDialog({
  state,
  pending,
  onClose,
  onConfirm,
}: {
  state: DialogState;
  pending: boolean;
  onClose: () => void;
  onConfirm: (row: AdminDropRow) => void;
}) {
  const open = state.kind === "force-retiring";
  const row = state.kind === "force-retiring" ? state.row : null;
  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Force "2 weeks left" early?</DialogTitle>
          <DialogDescription>
            {row ? (
              <>
                <strong>{row.portfolio_title}</strong> will be marked as{" "}
                <em>retiring</em> immediately. The retirement countdown badge
                will show on the public shop. Existing sales remain valid.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => row && onConfirm(row)}
            disabled={pending || !row}
          >
            {pending ? "Moving…" : "Force retiring"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForceCloseDialog({
  state,
  pending,
  onClose,
  onConfirm,
}: {
  state: DialogState;
  pending: boolean;
  onClose: () => void;
  onConfirm: (row: AdminDropRow, token: string) => void;
}) {
  const open = state.kind === "force-close";
  const row = state.kind === "force-close" ? state.row : null;
  const [typed, setTyped] = useState("");
  const matches = row ? typed.trim() === row.portfolio_title.trim() : false;

  function handleClose() {
    setTyped("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close this drop forever?</DialogTitle>
          <DialogDescription>
            This is a one-way trip. Once closed, a drop cannot be re-opened —
            it stays in the audit log but no new sales are possible. Existing
            sealed dossiers remain valid in their owners' vaults.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="confirm-title">
            Type the portfolio title to confirm:
          </Label>
          <Input
            id="confirm-title"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={row?.portfolio_title ?? ""}
            aria-describedby="confirm-hint"
            autoComplete="off"
          />
          <p id="confirm-hint" className="text-xs opacity-70">
            Type <strong>{row?.portfolio_title}</strong> exactly to enable the
            destructive button below.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => row && onConfirm(row, typed.trim())}
            disabled={pending || !row || !matches}
            aria-disabled={pending || !matches}
          >
            {pending ? "Closing…" : "Close drop forever"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
